/**
 * OpenSolar Settings Page
 *
 * This page allows the user to configure OpenSolar API credentials for real-time integration.
 * Users can either connect with API credentials or directly with a bearer token.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Loader2, AlertCircle, KeyRound, SaveAll, CheckCircle2, KeySquare } from 'lucide-react';
import { BearerTokenConnection } from '@/components/opensolar/BearerTokenConnection';

// Schema for OpenSolar API credentials
const openSolarAuthSchema = z.object({
  api_key: z.string().min(1, 'API Key is required'),
  email: z.string().email('Invalid email address').min(1, 'Email is required'),
  org_id: z.string().min(1, 'Organization ID is required'),
});

type OpenSolarAuthFormValues = z.infer<typeof openSolarAuthSchema>;

export default function OpenSolarSettingsPage() {
  const { toast } = useToast();
  const [testStatus, setTestStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState<string>('');

  // Form setup with Zod validation
  const form = useForm<OpenSolarAuthFormValues>({
    resolver: zodResolver(openSolarAuthSchema),
    defaultValues: {
      api_key: '',
      email: '',
      org_id: '',
    },
  });

  // Fetch existing settings
  const { data: settings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['/api/opensolar/settings'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/opensolar/settings');
        if (!response.ok) {
          throw new Error('Failed to fetch OpenSolar settings');
        }
        const data = await response.json();
        // Update form values with existing settings
        if (data) {
          form.reset({
            api_key: data.api_key || '',
            email: data.email || '',
            org_id: data.org_id || '',
          });
        }
        return data;
      } catch (error) {
        console.error('Error fetching OpenSolar settings:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to load existing OpenSolar settings',
        });
        throw error;
      }
    },
  });

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async (values: OpenSolarAuthFormValues) => {
      const response = await fetch('/api/opensolar/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save settings');
      }

      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Settings Saved',
        description: 'Your OpenSolar API credentials have been saved successfully.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save settings',
      });
    },
  });

  // Test API connection
  const testApiConnection = async () => {
    setTestStatus('loading');
    setTestMessage('');

    try {
      const response = await fetch('/api/opensolar/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form.getValues()),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'API connection test failed');
      }

      setTestStatus('success');
      setTestMessage('Connection successful! OpenSolar API credentials are valid.');
    } catch (error) {
      setTestStatus('error');
      setTestMessage(error instanceof Error ? error.message : 'Failed to test API connection');
    }
  };

  // Form submit handler
  const onSubmit = (values: OpenSolarAuthFormValues) => {
    saveSettingsMutation.mutate(values);
  };

  // Refresh all OpenSolar data after a successful connection
  const handleConnectionSuccess = () => {
    toast({
      title: 'Connection Successful',
      description: 'OpenSolar API connection has been established successfully.',
    });
  };

  return (
    <div className="container py-8">
      <h1 className="text-3xl font-bold mb-6">OpenSolar Integration Settings</h1>

      <Tabs defaultValue="api-key" className="w-full max-w-2xl mx-auto">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="api-key">
            <KeyRound className="h-4 w-4 mr-2" />
            API Key
          </TabsTrigger>
          <TabsTrigger value="bearer-token">
            <KeySquare className="h-4 w-4 mr-2" />
            Bearer Token
          </TabsTrigger>
        </TabsList>

        <TabsContent value="api-key">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" />
                API Credentials
              </CardTitle>
              <CardDescription>
                Configure your OpenSolar API credentials to enable real-time project viewing
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingSettings ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address</FormLabel>
                          <FormControl>
                            <Input placeholder="your-email@example.com" {...field} />
                          </FormControl>
                          <FormDescription>
                            The email address associated with your OpenSolar account
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="api_key"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>API Key</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Your OpenSolar API key"
                              type="password"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            The API key generated in your OpenSolar account settings
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="org_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organization ID</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. 12345" {...field} />
                          </FormControl>
                          <FormDescription>Your OpenSolar organization ID</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {testStatus === 'success' && (
                      <Alert className="bg-green-50 dark:bg-green-900/20">
                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <AlertTitle>Connection Successful</AlertTitle>
                        <AlertDescription>{testMessage}</AlertDescription>
                      </Alert>
                    )}

                    {testStatus === 'error' && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Connection Failed</AlertTitle>
                        <AlertDescription>{testMessage}</AlertDescription>
                      </Alert>
                    )}

                    <div className="flex justify-between pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={testApiConnection}
                        disabled={testStatus === 'loading' || saveSettingsMutation.isPending}
                      >
                        {testStatus === 'loading' && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Test Connection
                      </Button>

                      <Button type="submit" disabled={saveSettingsMutation.isPending}>
                        {saveSettingsMutation.isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        <SaveAll className="mr-2 h-4 w-4" />
                        Save Settings
                      </Button>
                    </div>
                  </form>
                </Form>
              )}
            </CardContent>
            <CardFooter className="flex justify-center border-t px-6 py-4">
              <p className="text-sm text-muted-foreground">
                These credentials will be used for all real-time OpenSolar API requests.
              </p>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="bearer-token">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeySquare className="h-5 w-5" />
                Bearer Token Connection
              </CardTitle>
              <CardDescription>
                Connect directly with an existing OpenSolar bearer token
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BearerTokenConnection onSuccess={handleConnectionSuccess} />
            </CardContent>
            <CardFooter className="flex justify-center border-t px-6 py-4">
              <p className="text-sm text-muted-foreground">
                If you already have a bearer token, you can connect directly without needing to
                generate one.
              </p>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
